import React from 'react';
import { motion } from 'framer-motion';
import { useCountdown } from '../hooks/useCountdown';
const fadeUp = {
  hidden: {
    opacity: 0,
    y: 24
  },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: i * 0.1,
      duration: 0.6,
      ease: 'easeOut'
    }
  })
};
export function InsaneValueSection() {
  const {
    isExpired
  } = useCountdown();
  return <div className="grain-section w-full bg-gradient-to-br from-[#0d1117] via-[#1a1a2e] to-[#0f172a] px-6 py-20 md:py-28 overflow-hidden" data-id="element-111">
      <div className="max-w-3xl mx-auto space-y-14" data-id="element-112">
        {/* Headline */}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true
      }} custom={0} variants={fadeUp} className="text-center space-y-4" data-id="element-113">
          <span className="inline-block bg-teal-500/20 border border-teal-500/40 text-teal-400 text-xs font-black tracking-widest uppercase px-4 py-1.5 rounded-full" data-id="element-114">
            Why This Is a No-Brainer
          </span>
          <h2 className="text-3xl md:text-5xl lg:text-6xl font-black tracking-tighter leading-[0.95] text-white" data-id="element-115">
            Here's What Makes This <span className="text-teal-400" data-id="element-116">INSANE</span>
            <br data-id="element-117" />
            <span className="text-white/70 text-2xl md:text-4xl font-bold" data-id="element-118">
              (And Why You'd Be CRAZY to Miss This)
            </span>
          </h2>
        </motion.div>

        {/* Normal Cost Block */}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true
      }} custom={1} variants={fadeUp} className="bg-white/5 border border-white/10 rounded-3xl p-8 md:p-10 space-y-4" data-id="element-119">
          <p className="text-white/60 text-sm font-bold tracking-widest uppercase" data-id="element-120">
            Normally, this costs:
          </p>
          <div className="flex flex-col sm:flex-row sm:items-end gap-3" data-id="element-121">
            <span className="text-5xl md:text-7xl font-black text-white/30 line-through decoration-red-400 decoration-4" data-id="element-122">
              $15,000–$40,000
            </span>
          </div>
          <p className="text-lg md:text-xl text-white/70 font-light leading-relaxed" data-id="element-123">
            That's what people <span className="font-bold text-white" data-id="element-124">PAY</span>{' '}
            to have their entire biology rebuilt from the inside out — a full
            Precision Cell™ Transformation Program from Dr. Ali or Brenden. And
            it takes <span className="font-semibold text-white" data-id="element-125">3+ months</span>{' '}
            to complete.
          </p>
        </motion.div>

        {/* Punchy Lines */}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true
      }} custom={2} variants={fadeUp} className="space-y-3 text-center" data-id="element-126">
          {['But for a limited time...', "We're building you a COMPLETE transformation program...", 'In ONE 2–3 hour session...'].map((line, i) => <motion.p key={i} custom={2 + i * 0.5} variants={fadeUp} initial="hidden" whileInView="visible" viewport={{
          once: true
        }} className={`text-xl md:text-2xl font-light text-white/80 leading-snug ${i === 1 ? 'font-bold text-white text-2xl md:text-3xl' : ''}`} data-id="element-127">
              {line}
            </motion.p>)}
        </motion.div>

        {/* Price Reveal */}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true
      }} custom={3} variants={fadeUp} className="text-center space-y-2" data-id="element-128">
          <p className="text-white/50 text-sm font-bold tracking-widest uppercase" data-id="element-129">
            Standard rate
          </p>
          <p className="text-4xl md:text-6xl font-black text-white/40 line-through decoration-red-400 decoration-4" data-id="element-130">
            $5,000
          </p>
        </motion.div>

        {/* Launch Special */}
        {!isExpired && <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true
      }} custom={4} variants={fadeUp} className="relative bg-gradient-to-br from-teal-500/20 to-teal-600/10 border border-teal-500/40 rounded-3xl p-8 md:p-12 text-center space-y-4 overflow-hidden" data-id="element-131">
            {/* Glow */}
            <div className="absolute inset-0 bg-teal-500/5 rounded-3xl pointer-events-none" data-id="element-132" />
            <p className="text-teal-400 text-sm font-black tracking-widest uppercase" data-id="element-133">
              Launch Special — Limited Time Only
            </p>
            <p className="text-white/80 text-xl md:text-2xl font-light" data-id="element-134">
              AND during this launch special?
              <br data-id="element-135" />
              <span className="font-bold text-white" data-id="element-136">
                We're offering it for the same price as our basic consult.
              </span>
            </p>
            <div className="flex flex-col items-center gap-1" data-id="element-137">
              <span className="text-white/40 text-lg font-medium" data-id="element-138">Just</span>
              <span className="text-7xl md:text-8xl font-black text-white tracking-tighter" data-id="element-139">
                $1,500
              </span>
            </div>

            {/* Savings Badges */}
            <div className="flex flex-col sm:flex-row gap-3 justify-center pt-2" data-id="element-140">
              <div className="bg-red-500/20 border border-red-400/30 rounded-2xl px-5 py-3 text-center" data-id="element-141">
                <p className="text-red-300 text-xs font-bold tracking-widest uppercase mb-0.5" data-id="element-142">
                  vs. Full Program
                </p>
                <p className="text-white font-black text-lg" data-id="element-143">
                  Save TENS of Thousands
                </p>
              </div>
              <div className="bg-amber-500/20 border border-amber-400/30 rounded-2xl px-5 py-3 text-center" data-id="element-144">
                <p className="text-amber-300 text-xs font-bold tracking-widest uppercase mb-0.5" data-id="element-145">
                  vs. Standard Rate
                </p>
                <p className="text-white font-black text-lg" data-id="element-146">Save $3,500</p>
              </div>
            </div>
          </motion.div>}

        {/* Closing Copy */}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true
      }} custom={5} variants={fadeUp} className="space-y-4 text-center" data-id="element-147">
          {["So if you've been stuck...", "If you've been spinning your wheels with \"experts\" who couldn't figure out what's wrong...", "And you want a precision roadmap built by people who've solved cases that stumped entire medical teams..."].map((line, i) => <p key={i} className={`text-lg md:text-xl leading-relaxed ${i === 2 ? 'font-semibold text-white text-xl md:text-2xl' : 'text-white/70 font-light'}`} data-id="element-148">
              {line}
            </p>)}
          <p className="text-2xl md:text-3xl font-black text-teal-400 pt-2" data-id="element-149">
            This is your shot.
          </p>
        </motion.div>

        {/* CTA */}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true
      }} custom={6} variants={fadeUp} className="flex justify-center pt-2" data-id="element-150">
          <a href="https://go.peptidescienceinstitute.com/meeting-with-the-master-advanced" className="w-full sm:w-auto px-10 py-5 bg-teal-600 hover:bg-teal-500 text-white text-lg md:text-xl font-bold rounded-full transition-all duration-200 shadow-lg hover:shadow-teal-500/30 hover:shadow-2xl hover:-translate-y-1 text-center" data-id="element-151">
            Book Your Meeting With the Master: Advanced
          </a>
        </motion.div>
      </div>
    </div>;
}